<template>
  <div class="flex flex-col rounded-lg border border-main p-4" data-testid="page-order-item">
    <div class="flex gap-2">
      <div class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden">
        <img v-if="item.product.imageUrl" :src="item.product.imageUrl" class="h-full w-full object-contain" />
      </div>
      <div class="flex-grow">
        <h3 class="line-clamp-1 txt-headline-m-semibold">{{ item.product.name }}</h3>
        <p v-if="!['Default', 'Standard'].includes(item.variant.name)" class="line-clamp-1">{{ item.variant.name }}</p>
      </div>
      <div class="w-[200px] text-right">
        <h3 class="txt-headline-m-semibold whitespace-nowrap">{{ formatPrice(totalWithDelivery, locale) }}</h3>
        <p class="text-body-xs whitespace-nowrap text-subtle">
          {{ formatPrice(item.price, locale) }} / {{ $t('FestivalShop.orders.manage.item.item') }}
        </p>
        <p v-if="deliveryFee > 0" class="text-body-xs whitespace-nowrap text-subtle">
          {{ formatPrice(deliveryFee, locale) }} {{ $t('BoothExtras.deliveryFee') }}
        </p>
      </div>
    </div>
    <div class="mt-4 flex items-center justify-between">
      <div class="flex gap-2">
        <template v-if="!expanded">
          <ChipBadge color="default" rounded size="m">
            {{
              `${$t('FestivalShop.orders.manage.item.items_title')}
            ${item.count}`
            }}
          </ChipBadge>
          <ChipBadge v-if="item.product.productCategory?.cancellationDate" color="default" rounded size="m">
            <template v-if="editable">
              {{ $t('FestivalShop.orders.manage.item.editable_until_title') }}
              {{ formatLocalDate(locale, item.product.productCategory?.cancellationDate) }}
            </template>
            <template v-else>
              <span class="flex items-center gap-1">
                {{ $t('FestivalShop.orders.manage.item.editable_until_closed') }}
                <TooltipWrapper v-if="showHistoricalSnapshotTooltip" top>
                  <template #trigger>
                    <CircleHelpIcon class="lucide-icon-s" />
                  </template>
                  <template #content>
                    <div class="max-w-56">{{ historicalSnapshotTooltip }}</div>
                  </template>
                </TooltipWrapper>
              </span>
            </template>
          </ChipBadge>
        </template>
      </div>
      <div class="flex gap-2.5">
        <button
          v-if="editable"
          class="btn-outline-neutral-xs min-w-0"
          :aria-label="$t('FestivalShop.orders.manage.item.edit_product')"
          @click="editProduct"
        >
          {{ $t('FestivalShop.orders.manage.item.edit_product') }}
          <SvgIcon :path="mdiPencilOutline" :size="16" class="" />
        </button>
        <button
          v-if="removable"
          class="btn-outline-neutral-xs min-w-0"
          :aria-label="$t('FestivalShop.orders.manage.item.remove_product')"
          @click="showRemoveConfirmation = true"
        >
          <SvgIcon :path="mdiTrashCanOutline" :size="16" class="" />
        </button>
      </div>
    </div>
    <ConfirmModal
      v-if="showRemoveConfirmation"
      :title="$t('Confirmation.defaultHeadline')"
      :approve="$t('FestivalShop.orders.manage.item.remove_product')"
      wrapper-class="rounded-lg"
      @close-dialog="handleRemoveConfirmation"
    />
  </div>
</template>
<script lang="ts" setup>
import { mdiPencilOutline, mdiTrashCanOutline } from '@mdi/js'
import { ChipBadge, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import ConfirmModal from '@/components/ConfirmModal.vue'
import { formatPrice } from '@/components/FestivalShop/helpers'
import SvgIcon from '@/components/SvgIcon.vue'
import { ExtrasOrdersDocument, useUpdateExtrasOrderMutation } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'
import { useLocaleStore } from '@/stores'

import { formatLocalDate, type ExtrasOrderItem } from '..'
import { getLockedSnapshotCount } from '../orderEditorItem'

const router = useRouter()
const route = useRoute()
const { locale } = storeToRefs(useLocaleStore())

const props = defineProps<{
  item: ExtrasOrderItem
  orderId: string
  eventSlug: string
  kioskId: string
}>()

const expanded = ref<boolean>(false)
const showRemoveConfirmation = ref<boolean>(false)
const { t } = useI18n()
const { mutate: updateExtrasOrder } = useUpdateExtrasOrderMutation()

const editable = computed(() => props.item.editable)
const removable = computed(() => props.item.canRemove)
const deliveryFee = computed(() => props.item.deliveryFee ?? 0)
const totalWithDelivery = computed(() => props.item.price * props.item.count + deliveryFee.value)
const showHistoricalSnapshotTooltip = computed(() => props.item.isHistoricalPriceSnapshot)
const historicalSnapshotTooltip = computed(() => t('FestivalShop.orders.manage.item.historical_snapshot_tooltip'))

const editProduct = () => {
  router.push({
    name: 'editOrderedProduct',
    params: {
      ...route.params,
      orderId: toPlainId(props.orderId),
      productId: toPlainId(props.item.product.id),
      categoryId: toPlainId(props.item.product.productCategory?.id ?? ''),
    },
  })
}

const handleRemoveConfirmation = (confirmed: boolean) => {
  showRemoveConfirmation.value = false
  if (confirmed) void removeProduct()
}

const removeProduct = async () => {
  try {
    const extrasOrderResult = await updateExtrasOrder(
      {
        kioskId: props.kioskId,
        extrasOrderId: props.orderId,
        rootEvent: { slug: props.eventSlug },
        extras: [
          {
            productId: props.item.product.id,
            variantId: props.item.variant.id,
            count: getLockedSnapshotCount(props.item),
          },
        ],
      },
      {
        refetchQueries: [
          {
            query: ExtrasOrdersDocument,
            variables: {
              rootEvent: { slug: props.eventSlug },
              kioskFilter: { id: props.kioskId },
            },
          },
        ],
        awaitRefetchQueries: true,
      },
    )

    const extrasOrderErrors = extrasOrderResult?.data?.updateExtrasOrder?.errors || []
    if (extrasOrderErrors.length > 0) {
      showNotification(extrasOrderErrors.join(', '), 'error')
      return
    }

    showNotification(t('FestivalShop.products.details.notification.order_updated'), 'success')
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  }
}
</script>
